import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import type {
  PatientBaseProfile,
  PatientHubListItem,
  PatientMeasurementRecord,
  PatientRecord,
} from '../types';
import {
  createPatientMeasurement,
  deletePatientById,
  deletePatientMeasurement,
  fetchPatientById,
  fetchPatientMeasurementsHistory,
  fetchPatientsList,
  updatePatientBaseProfile,
} from '../services/patientsService';

export const patientsQueryKeys = {
  all: ['patients'] as const,
  list: () => [...patientsQueryKeys.all, 'list'] as const,
  detail: (patientId: number) =>
    [...patientsQueryKeys.all, 'detail', patientId] as const,
  measurementsHistory: (patientId: number) =>
    [...patientsQueryKeys.all, 'measurements-history', patientId] as const,
};

export function usePatientsListQuery() {
  return useQuery({
    queryKey: patientsQueryKeys.list(),
    queryFn: fetchPatientsList,
    staleTime: 60 * 1000,
  });
}

export function usePatientQuery(patientId: number | null) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: patientsQueryKeys.detail(patientId ?? 0),
    queryFn: async () => {
      const fetched = await fetchPatientById(patientId!);
      const detailKey = patientsQueryKeys.detail(patientId!);
      const cached = queryClient.getQueryData<PatientRecord>(detailKey);

      if (cached == null) {
        return fetched;
      }

      const mergedProfile = {
        biologicalSex:
          fetched.profile.biologicalSex ?? cached.profile.biologicalSex,
        dateOfBirth: fetched.profile.dateOfBirth ?? cached.profile.dateOfBirth,
        heightCm: fetched.profile.heightCm ?? cached.profile.heightCm,
      };

      return {
        ...fetched,
        profile: mergedProfile,
      };
    },
    enabled: patientId != null,
    staleTime: 60 * 1000,
  });
}

export function usePatientMeasurementsHistoryQuery(patientId: number | null) {
  return useQuery({
    queryKey: patientsQueryKeys.measurementsHistory(patientId ?? 0),
    queryFn: () => fetchPatientMeasurementsHistory(patientId!),
    enabled: patientId != null,
  });
}

export function useDeletePatientMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (patientId: number) => deletePatientById(patientId),
    onSuccess: async (_, patientId) => {
      notifications.show({
        color: 'teal',
        title: 'Utente eliminato',
        message: "L'utente è stato rimosso dalla tua lista.",
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: patientsQueryKeys.list() }),
        queryClient.removeQueries({
          queryKey: patientsQueryKeys.detail(patientId),
        }),
        queryClient.removeQueries({
          queryKey: patientsQueryKeys.measurementsHistory(patientId),
        }),
      ]);
    },
    onError: error => {
      console.error('[patients] delete patient mutation failed', {
        error,
        message: error instanceof Error ? error.message : String(error),
      });
      notifications.show({
        color: 'red',
        title: 'Errore',
        message:
          error instanceof Error
            ? error.message
            : "Impossibile eliminare l'utente",
      });
    },
  });
}

export function useUpdatePatientBaseProfileMutation(patientId: number) {
  const queryClient = useQueryClient();
  const detailKey = patientsQueryKeys.detail(patientId);

  return useMutation({
    mutationFn: (profile: PatientBaseProfile) =>
      updatePatientBaseProfile(patientId, profile),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: detailKey });
    },
    onSuccess: async data => {
      await queryClient.cancelQueries({ queryKey: detailKey });

      queryClient.setQueryData(detailKey, data);

      queryClient.setQueryData<PatientHubListItem[]>(
        patientsQueryKeys.list(),
        current =>
          (current ?? []).map(item =>
            item.id === patientId
              ? { ...item, profile: data.profile, updatedAt: data.updatedAt }
              : item
          )
      );

      notifications.show({
        color: 'teal',
        title: 'Profilo aggiornato',
        message: "Il profilo base dell'utente è stato salvato.",
      });
    },
    onError: error => {
      console.error('[patients] update base profile mutation failed', {
        patientId,
        error,
        message: error instanceof Error ? error.message : String(error),
      });
      notifications.show({
        color: 'red',
        title: 'Errore',
        message:
          error instanceof Error
            ? error.message
            : 'Impossibile aggiornare il profilo base',
      });
    },
  });
}

export function useDeletePatientMeasurementMutation(patientId: number) {
  const queryClient = useQueryClient();
  const historyQueryKey = patientsQueryKeys.measurementsHistory(patientId);

  const matchesMeasurementId = (
    item: PatientMeasurementRecord,
    measurementId: number
  ) => Number(item.id) === Number(measurementId);

  const removeMeasurementFromCache = (measurementId: number) => {
    queryClient.setQueryData<PatientMeasurementRecord[]>(
      historyQueryKey,
      current =>
        (current ?? []).filter(
          item => !matchesMeasurementId(item, measurementId)
        )
    );
  };

  return useMutation({
    mutationFn: (measurementId: number) =>
      deletePatientMeasurement(patientId, measurementId),
    onMutate: async measurementId => {
      await queryClient.cancelQueries({ queryKey: historyQueryKey });

      const previousHistory =
        queryClient.getQueryData<PatientMeasurementRecord[]>(historyQueryKey);

      removeMeasurementFromCache(measurementId);

      return { previousHistory };
    },
    onSuccess: async (_, measurementId) => {
      removeMeasurementFromCache(measurementId);

      await queryClient.refetchQueries({ queryKey: historyQueryKey });

      notifications.show({
        color: 'teal',
        title: 'Rilevazione eliminata',
        message: 'La rilevazione è stata rimossa dallo storico.',
      });
    },
    onError: (error, _measurementId, context) => {
      if (context?.previousHistory != null) {
        queryClient.setQueryData(historyQueryKey, context.previousHistory);
      }

      notifications.show({
        color: 'red',
        title: 'Errore',
        message:
          error instanceof Error
            ? error.message
            : 'Impossibile eliminare la rilevazione',
      });
    },
  });
}

export function useCreatePatientMeasurementMutation(patientId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: PatientMeasurementRecord) =>
      createPatientMeasurement(patientId, values),
    onSuccess: async data => {
      const currentHistory =
        queryClient.getQueryData<PatientMeasurementRecord[]>(
          patientsQueryKeys.measurementsHistory(patientId)
        ) ?? [];

      queryClient.setQueryData(
        patientsQueryKeys.measurementsHistory(patientId),
        [data, ...currentHistory]
      );

      await queryClient.invalidateQueries({
        queryKey: patientsQueryKeys.measurementsHistory(patientId),
      });

      notifications.show({
        color: 'teal',
        title: 'Rilevazione salvata',
        message: 'La nuova rilevazione è stata registrata.',
      });
    },
    onError: error => {
      notifications.show({
        color: 'red',
        title: 'Errore',
        message:
          error instanceof Error
            ? error.message
            : 'Impossibile salvare la rilevazione',
      });
    },
  });
}
